import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, Clock, PlayCircle, Check, ArrowLeft, BadgeCheck, Users } from "lucide-react";
import api, { apiError } from "../lib/api";
import BlurImage from "../components/BlurImage";
import CourseReviews from "../components/CourseReviews";
import { loadRazorpay, startCheckout } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { toast } from "sonner";

export default function CourseDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { openNotify, openPaymentSoon } = useSite();
    const [course, setCourse] = useState(null);
    const [busy, setBusy] = useState(false);
    const [enrolled, setEnrolled] = useState(false);

    useEffect(() => { api.get(`/courses/${slug}`).then((r) => { const c = r.data?.course || r.data; if (c?.landing && c.landing.startsWith("/")) { window.location.replace(c.landing + (window.location.hash || "")); return; } setCourse(r.data); }).catch(() => setCourse(false)); }, [slug]);
    useEffect(() => {
        if (!user) { setEnrolled(false); return; }
        api.get("/me/enrollments").then((r) => setEnrolled(r.data.some((e) => e.course_slug === slug))).catch(() => {});
    }, [user, slug]);

    const enroll = async () => {
        if (!user) { navigate(`/login?next=/courses/${slug}`); return; }
        setBusy(true);
        await startCheckout({ kind: "course", slug: course.slug, onSuccess: () => { setEnrolled(true); navigate(`/learn/${course.slug}`); } });
        setBusy(false);
    };

    if (course === false) return <div className="mx-container py-40 text-center text-neutral-500">Course not found.</div>;
    if (!course) return <div className="mx-container py-40 text-center"><div className="h-8 w-8 mx-auto rounded-full border-2 border-neutral-200 border-t-ink animate-spin" /></div>;

    return (
        <div data-testid="course-detail-page">
            <section className="bg-cream py-16 lg:py-24 border-b border-[#e8e1d4]">
                <div className="mx-container grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <Link to="/courses" className="inline-flex items-center gap-1.5 text-neutral-400 hover:text-ink text-sm mb-6"><ArrowLeft className="h-4 w-4" /> All courses</Link>
                        <p className="overline mb-4">{course.category} · {course.level}</p>
                        <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-6xl leading-[1.0]">{course.title}</h1>
                        <p className="mt-4 text-neutral-500 text-lg max-w-xl">{course.subtitle}</p>
                        <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-neutral-600">
                            {!course.coming_soon && Number(course.reviews) > 0 && <a href="#reviews" onClick={(e) => { e.preventDefault(); document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} className="flex items-center gap-1.5 underline underline-offset-4 decoration-dotted hover:text-ink" data-testid="course-rating-link"><Star className="h-4 w-4 fill-brand-gold text-brand-gold" />{course.rating} ({course.reviews} {Number(course.reviews) === 1 ? "review" : "reviews"})</a>}
                            {!course.coming_soon && Number(course.students) > 0 && <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />{Number(course.students).toLocaleString("en-IN")} students</span>}
                            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{course.duration}</span>
                            <span className="flex items-center gap-1.5"><PlayCircle className="h-4 w-4" />{course.lessons} lessons</span>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative overflow-hidden aspect-[16/10]">
                            <BlurImage src={course.cover} alt={course.title} className="h-full w-full object-cover" eager />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-white">
                <div className="mx-container grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-8">
                        <h2 className="font-display font-bold text-2xl mb-4">What you'll learn</h2>
                        <ul className="grid sm:grid-cols-2 gap-3 mb-14">
                            {(course.outcomes || []).map((o) => (
                                <li key={o} className="flex items-start gap-2.5 text-neutral-700"><Check className="h-5 w-5 text-brand-yellow shrink-0" /> {o}</li>
                            ))}
                        </ul>
                        <h2 className="font-display font-bold text-2xl mb-4">Curriculum</h2>
                        <div className="border border-neutral-200 divide-y divide-neutral-200">
                            {course.modules.map((m, i) => (
                                <div key={i} className="flex items-center justify-between px-5 py-4">
                                    <div className="flex items-center gap-4">
                                        <span className="big-index text-xl text-neutral-300">{String(i + 1).padStart(2, "0")}</span>
                                        <span className="font-semibold">{m.title}</span>
                                    </div>
                                    <span className="text-sm text-neutral-400">{m.lessons} lessons</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-4">
                        <div className="sticky top-24 border border-[#e8e1d4] p-6 bg-white">
                            {!course.coming_soon && <p className="font-display font-semibold text-3xl">₹{course.price.toLocaleString("en-IN")}</p>}
                            {!course.coming_soon && Number(course.original_price) > Number(course.price) && <p className="text-neutral-400 line-through">₹{Number(course.original_price).toLocaleString("en-IN")}</p>}
                            {course.coming_soon ? (
                                <>
                                    <span className="inline-block mb-3 text-[11px] font-bold uppercase tracking-[0.16em] bg-cream border border-[#e0d6c2] text-brand-golddark px-2.5 py-1">Coming soon</span>
                                    <button onClick={() => openNotify({ kind: "course", slug: course.slug, title: course.title })} className="btn-primary w-full" data-testid="course-notify-btn">Notify Me</button>
                                    <p className="mt-3 text-xs text-neutral-500">Enrolment opens soon. Join the list to be first in.</p>
                                </>
                            ) : enrolled ? (
                                <Link to={`/learn/${slug}`} className="btn-primary w-full mt-5" data-testid="course-continue-btn">Continue Learning</Link>
                            ) : (
                                <button onClick={enroll} disabled={busy} className="btn-primary w-full mt-5 disabled:opacity-60" data-testid="course-enroll-btn">
                                    {busy ? "Please wait…" : "Start Learning"}
                                </button>
                            )}
                            <ul className="mt-6 space-y-2.5 text-sm text-neutral-600">
                                <li className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-brand-golddark" />{Number(course.validity_days) > 0 ? (Number(course.validity_days) % 30 === 0 ? `${Number(course.validity_days) / 30} months of access` : `${course.validity_days} days of access`) : "Lifetime access"}</li>
                                <li className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-brand-golddark" />Certificate of completion</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            <div className="mx-container"><CourseReviews slug={course.slug} className="py-12 lg:py-16" showEmpty={Number(course.reviews) > 0} /></div>
        </div>
    );
}
